import * as React from 'react';
import { useTranslation } from 'next-i18next';
import { Controller } from 'react-hook-form';
import { useAtom } from 'jotai';

import { Button as ButtonV2, Heading, RadioGroup } from '@restworld/ui-ds-base';

import { CollaboratorModalFooterContext } from '../collaborator-modal-footer-context';
import {
  StyledForm,
  ButtonsWrapper,
  FooterButtonsWrapper,
  ContentAndInputsWrapper,
  ContentWrapper,
  InputWrapper,
  InputsWrapper,
} from '../add-collaborator-flow/collaborator-details-form.styled';
import usePermissionLevelsList from '../../../hooks/use-collaborator-permission-level-list';
import useCollaboratorFormTitles from '../../../hooks/use-collaborator-form-titles';
import { selectedCollaboratorAtom, useCollaboratorActionModal } from '../../../collaborators.atoms';
import useEditCollaboratorPermissionLevelForm from '../../../hooks/use-edit-collaborator-permission-level-form';
import { useMemo } from 'react';

function EditCollaboratorPermissionLevelForm() {
  const { t } = useTranslation('collaborators-page');
  const footerContext = React.useContext(CollaboratorModalFooterContext);

  const permissionLevels = usePermissionLevelsList();
  const { formSubtitleEl } = useCollaboratorFormTitles();
  const [selectedCollaborator] = useAtom(selectedCollaboratorAtom);

  const { currentPermissionLevel, firstName, userId } = useMemo(() => {
    if (selectedCollaborator) {
      const {
        user_id,
        role_rw_key,
        user: { name },
      } = selectedCollaborator;
      return {
        userId: user_id,
        currentPermissionLevel: role_rw_key,
        firstName: name,
      };
    } else {
      return {};
    }
  }, [selectedCollaborator]);

  const { control, handleSubmit, isValid, onSubmit, isPending } = useEditCollaboratorPermissionLevelForm(
    userId,
    currentPermissionLevel
  );
  const { closeCollaboratorActionModal } = useCollaboratorActionModal();

  const footerEl = React.useMemo(
    () => (
      <FooterButtonsWrapper>
        <ButtonV2 type="button" variant="tertiary" onClick={closeCollaboratorActionModal}>
          {t('back')}
        </ButtonV2>
        <ButtonV2 type="button" loading={isPending} disabled={!isValid} onClick={handleSubmit(onSubmit)}>
          {t('save_changes')}
        </ButtonV2>
      </FooterButtonsWrapper>
    ),
    [closeCollaboratorActionModal, isValid, isPending, onSubmit, t]
  );

  React.useEffect(() => {
    if (footerContext) {
      footerContext.setFooter(footerEl);
      return () => footerContext.setFooter(null);
    }
  }, [footerContext, footerEl]);

  return (
    <StyledForm>
      <ContentAndInputsWrapper>
        <Heading color="neutral" className="w-full text-base lg:text-lg">
          {t('edit_permission_title', { firstName })}
        </Heading>
        <ContentWrapper>{formSubtitleEl}</ContentWrapper>
        <InputsWrapper>
          <InputWrapper>
            <Controller
              name="permissionLevel"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <RadioGroup
                  options={permissionLevels}
                  id="permissionLevel"
                  value={field.value}
                  onChange={field.onChange}
                  orientation="horizontal"
                  // Center the permission chips (like master). className reaches only the RadioGroup's
                  // outer wrapper, so target its inner <ul role="radiogroup"> via an arbitrary variant
                  // rather than changing the DS default (used left-aligned by ~20 other horizontal groups).
                  className="[&_ul]:justify-center"
                  {...field}
                />
              )}
            />
          </InputWrapper>
        </InputsWrapper>
      </ContentAndInputsWrapper>
      {!footerContext && (
        <ButtonsWrapper>
          <ButtonV2 type="button" variant="tertiary" onClick={closeCollaboratorActionModal}>
            {t('back')}
          </ButtonV2>
          <ButtonV2 type="button" loading={isPending} disabled={!isValid} onClick={handleSubmit(onSubmit)}>
            {t('save_changes')}
          </ButtonV2>
        </ButtonsWrapper>
      )}
    </StyledForm>
  );
}
export default EditCollaboratorPermissionLevelForm;
